import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import { post, get, del } from 'aws-amplify/api';
import { signOut, getCurrentUser, setUpTOTP, verifyTOTPSetup, updateMFAPreference, associateWebAuthnCredential, listWebAuthnCredentials, deleteWebAuthnCredential, fetchUserAttributes, fetchMFAPreference } from "aws-amplify/auth";
import { PASSKEY_RP_ID, isOnPasskeyDomain } from "./utils/passkey";
import { isNewVersionAvailable } from "./utils/appVersion";
import QRCode from 'qrcode';
import Dashboard from "./components/Dashboard";
import VideoManualGenerator from "./components/VideoManualGenerator";
import OperatorConsole from "./components/OperatorConsole";
import Settings from "./components/Settings";
import UserMenu from "./components/UserMenu";
import Login from "./components/Login";
import LandingPage from "./components/LandingPage";
import UserProfile from "./components/UserProfile";
import Home from "./components/Home";
import { getLocalDateString } from './utils/date';

// ブラウザ終了時に未保存セッションを保持するlocalStorageキー
const PENDING_SESSION_KEY = 'worktimer_pending_session';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  const [loginInitialMode, setLoginInitialMode] = useState('login');
  const [user, setUser] = useState(null);
  const [dbUser, setDbUser] = useState(null); // ← データベースから取得したユーザー情報
  const [isOperator, setIsOperator] = useState(false); // 運営者（全テナントの管理画面を使える）かどうか
  const [loading, setLoading] = useState(true);
  const [loginError, setLoginError] = useState('');

  const [isActive, setIsActive] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [startTime, setStartTime] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  // 表示中の画面: 'home' | 'timer' | 'dashboard' | 'videoManuals' | 'settings' | 'profile' | 'operator'
  // ログイン直後は HOME を表示する
  const [currentPage, setCurrentPage] = useState('home');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // 未保存セッションの復旧ダイアログ
  const [pendingSession, setPendingSession] = useState(null);
  // 作業カウント機能
  const [completedCount, setCompletedCount] = useState(0);      // 現セッション分のカウント
  const [todayBaseCount, setTodayBaseCount] = useState(0);      // 過去ログの今日の累計完了数
  const [todayAssignment, setTodayAssignment] = useState(null); // { assigned_count, task_id }
  // スリープ防止（Wake Lock）
  const [wakeLockEnabled, setWakeLockEnabled] = useState(true);
  const wakeLockRef = useRef(null);

  const isRequestInProgress = useRef(false);

  // MFA必須設定
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetupStep, setMfaSetupStep] = useState('intro'); // 'intro' | 'scan' | 'done'
  const [passkeyReRegister, setPasskeyReRegister] = useState(false); // ドメイン変更に伴うパスキー再登録中か
  const [adminTotpNeeded, setAdminTotpNeeded] = useState(false); // 管理者の認証アプリ（パスワードでログインしたときの二段階目）が未登録か
  const [mfaQrUrl, setMfaQrUrl] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);

  // セッションタイムアウト
  const sessionTimerRef = useRef(null);

  // カスタムダイアログの状態
  // { icon, title, message, confirmLabel, cancelLabel, onConfirm, onCancel }
  // cancelLabel が null の場合は「閉じる」のみのアラート
  const [appDialog, setAppDialog] = useState(null);
  const dialogResolveRef = useRef(null);

  // アラートダイアログ（OKのみ）
  const showAlert = (icon, title, message) =>
    new Promise(resolve => {
      dialogResolveRef.current = resolve;
      setAppDialog({ icon, title, message, confirmLabel: '閉じる', cancelLabel: null,
        onConfirm: () => { setAppDialog(null); resolve(false); },
        onCancel: null,
      });
    });

  // 確認ダイアログ（OK / キャンセル）→ true/false を返す
  const showConfirm = (icon, title, message, confirmLabel = 'OK', cancelLabel = 'キャンセル', danger = false) =>
    new Promise(resolve => {
      dialogResolveRef.current = resolve;
      setAppDialog({
        icon, title, message, confirmLabel, cancelLabel, danger,
        onConfirm: () => { setAppDialog(null); resolve(true); },
        onCancel:  () => { setAppDialog(null); resolve(false); },
      });
    });

  // 認証状態チェック
  useEffect(() => {
    checkAuthState();
  }, []);

  // ログイン前の画面（紹介ページ・ログイン画面）では、新しい版が公開されていれば読み込み直す
  // （古い版のままログインすると、新しく追加した確認画面などが出ないため）
  useEffect(() => {
    if (loading || isAuthenticated) return;
    let cancelled = false;
    const check = async () => {
      if (!cancelled && await isNewVersionAvailable()) window.location.reload();
    };
    check();
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVisible); };
  }, [loading, isAuthenticated]);

  // Stripe決済完了リダイレクト検知
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'success') {
      window.history.replaceState({}, '', window.location.pathname);
      setAppDialog({
        icon: '🎉',
        title: 'お支払い完了',
        message: 'お支払いが完了しました！\nプランが有効化されます（数分かかる場合があります）\n設定 → プラン・課金 でご確認ください。',
        confirmLabel: '閉じる',
        cancelLabel: null,
        onConfirm: () => setAppDialog(null),
        onCancel: null,
      });
    }
  }, [isAuthenticated]);

  // Wake Lock: スリープ防止
  useEffect(() => {
    const acquire = async () => {
      if (!('wakeLock' in navigator)) return;
      if (wakeLockEnabled) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        } catch (e) {
          console.warn('Wake Lock 取得失敗:', e);
        }
      } else {
        if (wakeLockRef.current) {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
        }
      }
    };
    acquire();
  }, [wakeLockEnabled]);

  // 画面が再び表示されたときに再取得（ブラウザ仕様上必要）
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && wakeLockEnabled && 'wakeLock' in navigator) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        } catch (e) {
          console.warn('Wake Lock 再取得失敗:', e);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [wakeLockEnabled]);

  // セッション非操作タイムアウト
  useEffect(() => {
    if (!isAuthenticated || mfaRequired) return;
    const timeoutMs = (dbUser?.session_timeout_minutes || 60) * 60 * 1000;

    const reset = () => {
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
      sessionTimerRef.current = setTimeout(async () => {
        try { await signOut(); } catch {}
        setIsAuthenticated(false);
        setUser(null);
        setDbUser(null);
        setCurrentPage('home');
      }, timeoutMs);
    };

    const events = ['mousemove', 'click', 'keypress', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, reset));
    reset();

    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    };
  }, [isAuthenticated, mfaRequired, dbUser?.session_timeout_minutes]);

  const checkAuthState = async () => {
    setLoading(true);
    try {
      const currentUser = await getCurrentUser();

      const userProfile = await fetchUserProfile();
      await fetchTasks();

      // MFA必須チェック: CognitoではなくDBのmfa_enabledで判定（Cognito MFA設定に依存しない）
      let mfaNeeded = !userProfile?.mfa_enabled;

      // 管理者のパスキーが現在の関連付けドメイン用でない場合（ドメイン変更前に登録したもの）は再登録が必要
      if (!mfaNeeded && userProfile?.role === 'admin' && userProfile?.mfa_type === 'webauthn' && isOnPasskeyDomain()) {
        try {
          const { credentials } = await listWebAuthnCredentials();
          if (!credentials.some((c) => c.relyingPartyId === PASSKEY_RP_ID)) {
            mfaNeeded = true;
            setPasskeyReRegister(true);
          }
        } catch (e) {
          console.warn('パスキー一覧の取得に失敗:', e.name, e.message);
        }
      }

      // 管理者は認証アプリも必須（パスワードでログインしたときに Cognito が二段階目として要求する。サーバー側でも強制）
      // 判定はサーバーの結果（API の強制と同じもの）を優先し、取れなかった場合だけブラウザから Cognito に確認する。
      // どちらも確認できなければ、安全側に倒して登録画面を出す（登録済みならコード確認ですぐ抜けられる）
      let totpNeeded = false;
      if (userProfile?.role === 'admin') {
        if (typeof userProfile.admin_totp_required === 'boolean') {
          totpNeeded = userProfile.admin_totp_required;
        } else {
          try {
            const pref = await fetchMFAPreference();
            totpNeeded = !(pref?.enabled || []).includes('TOTP');
          } catch (e) {
            console.warn('MFA設定の取得に失敗:', e.name, e.message);
            totpNeeded = true;
          }
        }
      }
      setAdminTotpNeeded(totpNeeded);

      setUser(currentUser);
      setIsAuthenticated(true);
      if (mfaNeeded) {
        setMfaRequired(true);
        setMfaSetupStep('intro');
      } else if (totpNeeded) {
        setMfaRequired(true);
        setMfaSetupStep('admin-totp');
      }

      // ブラウザ終了で保存できなかったセッションが残っていれば復旧ダイアログを表示
      try {
        const saved = localStorage.getItem(PENDING_SESSION_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          // end_at がない場合は「閉じた時刻不明」として現在時刻をデフォルトに
          if (!parsed.end_at) parsed.end_at = new Date().toISOString();
          setPendingSession(parsed);
        }
      } catch {
        localStorage.removeItem(PENDING_SESSION_KEY);
      }
    } catch (e) {
      setIsAuthenticated(false);
      setUser(null);
      setDbUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Cognito上の確認済みメールアドレスとDBのメールアドレスがずれていたらDBを合わせる
  // （メール変更の確認後に画面を閉じた場合や、管理コンソールから直接変更した場合の取りこぼし対策）
  const syncEmailFromCognito = async (dbEmail) => {
    try {
      const attrs = await fetchUserAttributes();
      if (!attrs.email || attrs.email_verified !== 'true') return;
      if ((attrs.email || '').toLowerCase() === (dbEmail || '').toLowerCase()) return;
      const op = post({ apiName: 'worktimerApi', path: '/users/me/sync-email' });
      const { body } = await op.response;
      const data = await body.json();
      if (data?.email) setDbUser(prev => prev ? { ...prev, email: data.email } : prev);
    } catch (e) {
      console.error('メールアドレス同期エラー:', e);
    }
  };

  // データベースからユーザー情報を取得（ユーザーオブジェクトを返す）
  const fetchUserProfile = async () => {
    try {
      const restOperation = get({
        apiName: 'worktimerApi',
        path: '/users'
      });
      const response = await restOperation.response;
      const users = await response.body.json();

      if (users && users.length > 0) {
        setDbUser(users[0]);
        fetchOperatorStatus();
        syncEmailFromCognito(users[0].email);
        return users[0];
      }
    } catch (e) {
      // 凍結ユーザー・IP制限対象の場合はサインアウトしてログイン画面へ
      let isFrozen = false;
      let isIpRestricted = false;
      try {
        const status = e.response?.statusCode;
        if (status === 403) {
          const body = await e.response.body.json();
          isFrozen = body.error === 'user_frozen';
          isIpRestricted = body.error === 'ip_restricted';
        }
      } catch {}
      if (isFrozen || isIpRestricted) {
        try { await signOut(); } catch {}
        setLoginError(isIpRestricted
          ? '許可されていないIPアドレスからはログインできません'
          : 'ユーザ名・パスワードが異なります');
        setIsAuthenticated(false);
        setUser(null);
        setDbUser(null);
        return null;
      }
      console.error("❌ ユーザー情報取得エラー:", e);
    }
    return null;
  };

  const handleLoginSuccess = async () => {
    // ログイン画面を開いている間に新しい版が公開された場合は、記録を済ませてから読み込み直す
    // （ログイン状態は保持されるので、読み込み後はそのままログイン済みで始まる）
    if (await isNewVersionAvailable()) {
      try {
        await post({ apiName: 'worktimerApi', path: '/auth/login-event', options: { body: {} } }).response;
      } catch (_) {}
      window.location.reload();
      return;
    }
    checkAuthState().then(() => {
      // ログインイベントを非同期で記録（失敗しても無視）
      post({ apiName: 'worktimerApi', path: '/auth/login-event', options: { body: {} } })
        .response
        .catch(() => {});
    });
  };

  // 画面遷移（メニューは閉じる）
  const fetchOperatorStatus = async () => {
    try {
      const op = get({ apiName: 'worktimerApi', path: '/tenants/operator/me' });
      const { body } = await op.response;
      const data = await body.json();
      setIsOperator(!!data.is_operator);
    } catch (e) {
      setIsOperator(false);
    }
  };

  const navigate = (page) => {
    setCurrentPage(page);
    setUserMenuOpen(false);
    window.scrollTo(0, 0);
  };

  // HOMEの「このタスクを始める」：タスクを選んだ状態でタイマー画面へ
  // （計測中の場合は勝手に切り替えず、タイマー画面を開くだけにする）
  const handleStartTaskFromHome = (taskId) => {
    if (taskId && !isActive) handleTaskChange(taskId);
    navigate('timer');
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setIsAuthenticated(false);
      setUser(null);
      setDbUser(null);
      setIsOperator(false);
      setCurrentPage('home');
    } catch (e) {
      console.error('ログアウトエラー:', e);
    }
  };

  // MFA必須セットアップ処理
  const startMfaSetup = async () => {
    // 既にシークレットが生成済みならAPIを再呼び出しせずそのまま使う（毎回新シークレット生成防止）
    if (mfaSecret && mfaQrUrl) {
      setMfaSetupStep('scan');
      return;
    }
    setMfaLoading(true);
    setMfaError('');
    try {
      const totpSetup = await setUpTOTP();
      const secret = totpSetup.sharedSecret;
      // emailではなくusernameを使用（DB mapping不一致でも正確な識別子）
      const accountName = dbUser?.username || user?.username || dbUser?.email || user?.signInDetails?.loginId || 'user';
      const issuer = 'FACTime';
      const otpauth = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountName)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;
      const qrUrl = await QRCode.toDataURL(otpauth);
      setMfaSecret(secret);
      setMfaQrUrl(qrUrl);
      setMfaSetupStep('scan');
    } catch (e) {
      setMfaError('MFAの設定開始に失敗しました。再度お試しください。');
    } finally {
      setMfaLoading(false);
    }
  };

  const verifyMfaSetup = async () => {
    if (!mfaCode.trim() || mfaCode.length !== 6) {
      setMfaError('6桁のコードを入力してください');
      return;
    }
    setMfaLoading(true);
    setMfaError('');
    try {
      await verifyTOTPSetup({ code: mfaCode });
    } catch (e) {
      console.error('❌ verifyTOTPSetup error:', e.name, e.message);
      if (e.name === 'EnableSoftwareTokenMFAException') {
        setMfaError('コードが正しくありません。認証アプリの最新の6桁コードを入力してください。');
      } else {
        setMfaError(`コードの確認に失敗しました（${e.name}）。再度お試しください。`);
      }
      setMfaLoading(false);
      return;
    }
    // Cognito preference更新（プール設定によっては失敗することがあるが続行）
    try {
      await updateMFAPreference({ totp: 'PREFERRED' });
    } catch (e) {
      console.warn('MFA preference update failed (pool setting may be Off):', e);
    }
    // DBにMFA完了を記録
    try {
      await post({ apiName: 'worktimerApi', path: '/users/me/mfa-complete', options: { body: { mfa_type: 'totp' } } }).response;
      setDbUser(prev => prev ? { ...prev, mfa_enabled: true, mfa_type: prev.role === 'admin' && prev.mfa_type === 'webauthn' ? 'webauthn' : 'totp' } : prev);
    } catch (e) {
      console.error('MFA DB記録エラー:', e);
    }
    setAdminTotpNeeded(false);
    setMfaLoading(false);
    setMfaSetupStep('done');
  };

  // 管理者向け: パスキー(WebAuthn)登録（ブラウザの生体認証/セキュリティキーダイアログを内包）
  const startWebAuthnSetup = async () => {
    setMfaLoading(true);
    setMfaError('');
    try {
      await associateWebAuthnCredential();
      // 他ドメイン用の古いパスキーは使えないので削除する（残っているとログイン時に失敗するパスキーが提示される）
      try {
        const { credentials } = await listWebAuthnCredentials();
        for (const c of credentials) {
          if (c.relyingPartyId !== PASSKEY_RP_ID && c.credentialId) {
            await deleteWebAuthnCredential({ credentialId: c.credentialId });
          }
        }
      } catch (cleanupErr) {
        console.warn('古いパスキーの削除に失敗:', cleanupErr.name, cleanupErr.message);
      }
      setPasskeyReRegister(false);
      await post({ apiName: 'worktimerApi', path: '/users/me/mfa-complete', options: { body: { mfa_type: 'webauthn' } } }).response;
      setDbUser(prev => prev ? { ...prev, mfa_enabled: true, mfa_type: 'webauthn' } : prev);
      setMfaSetupStep(adminTotpNeeded ? 'admin-totp' : 'done');
    } catch (e) {
      console.error('❌ associateWebAuthnCredential error:', e.name, e.message);
      setMfaError(
        e.name === 'NotAllowedError'
          ? 'パスキーの作成がキャンセルされました。もう一度お試しください。'
          : 'パスキーの登録に失敗しました。端末が生体認証/PINに対応しているかご確認ください。'
      );
    } finally {
      setMfaLoading(false);
    }
  };

  // タスク一覧を取得
  const fetchTasks = async () => {
    setTasksLoading(true);
    try {
      const restOperation = get({
        apiName: 'worktimerApi',
        path: '/tasks'
      });
      const response = await restOperation.response;
      const data = await response.body.json();
      setTasks(data);
    } catch (e) {
      console.error("❌ タスク取得エラー:", e);
    } finally {
      setTasksLoading(false);
    }
  };

  // 今日の割り当て＆累計完了数を取得（特定タスク用）
  const fetchTodayAssignment = async (taskId) => {
    if (!taskId) {
      setTodayAssignment(null);
      setTodayBaseCount(0);
      return;
    }
    const today = getLocalDateString();
    // 割り当て取得
    try {
      const op = get({ apiName: 'worktimerApi', path: '/task-assignments', options: { queryParams: { date: today } } });
      const response = await op.response;
      const data = await response.body.json();
      const assignment = (Array.isArray(data) ? data : []).find(a => a.task_id.trim() === taskId.trim());
      setTodayAssignment(assignment || null);
    } catch (e) {
      console.error("❌ 割り当て取得エラー:", e);
      setTodayAssignment(null);
    }
    // 今日の累計完了数を取得（過去のワークログ分）
    try {
      const op2 = get({ apiName: 'worktimerApi', path: '/worklogs/today-count', options: { queryParams: { task_id: taskId, date: today } } });
      const response2 = await op2.response;
      const data2 = await response2.body.json();
      setTodayBaseCount(Number(data2.total_completed) || 0);
    } catch (e) {
      console.error("❌ 本日累計完了数取得エラー:", e);
      setTodayBaseCount(0);
    }
  };

  // 完了カウント変更（delta: 1 or -1）
  const changeCount = async (delta) => {
    if (!isActive) return;
    try {
      const op = post({
        apiName: 'worktimerApi',
        path: '/active-sessions/count',
        options: { body: { delta } }
      });
      const response = await op.response;
      const data = await response.body.json();
      if (data.completed_count !== undefined) {
        setCompletedCount(data.completed_count);
      }
    } catch (e) {
      console.error("❌ カウント変更エラー:", e);
    }
  };

  const handleCountUp = () => changeCount(1);
  const handleCountDown = () => changeCount(-1);

  useEffect(() => {
    let interval = null;
    if (isActive) {
      interval = setInterval(() => {
        setSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isActive]);

  // ブラウザを閉じる直前に終了時刻をlocalStorageへ追記
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isActive && startTime && selectedTaskId) {
        const taskName = tasks.find(t => t.task_id === selectedTaskId)?.task_name || '';
        localStorage.setItem(PENDING_SESSION_KEY, JSON.stringify({
          task_id: selectedTaskId,
          task_name: taskName,
          start_at: startTime.toISOString(),
          end_at: new Date().toISOString(),
        }));
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isActive, startTime, selectedTaskId, tasks]);

  // 計測中に別のタスクへ切り替える
  const handleTaskChange = async (newTaskId) => {
    if (newTaskId === selectedTaskId) return;

    if (!isActive) {
      // 計測していなければ単純に選択変更
      setSelectedTaskId(newTaskId);
      setCompletedCount(0);
      fetchTodayAssignment(newTaskId);
      return;
    }

    if (isSaving || isRequestInProgress.current) return;
    isRequestInProgress.current = true;
    setIsSaving(true);

    const endTime = new Date();
    const currentStartTime = startTime || new Date();

    try {
      // 現在のタスクのworklogを保存
      const saveOp = post({
        apiName: 'worktimerApi',
        path: '/worklogs',
        options: {
          body: {
            task_id: selectedTaskId,
            start_at: currentStartTime.toISOString(),
            end_at: endTime.toISOString(),
            completed_count: completedCount,
          }
        }
      });
      await saveOp.response;

      // アクティブセッションを削除
      try {
        const delOp = del({ apiName: 'worktimerApi', path: '/active-sessions' });
        await delOp.response;
      } catch {}

      // 新しいタスクでアクティブセッションを登録
      const now = new Date();
      const newTaskName = tasks.find(t => t.task_id === newTaskId)?.task_name || '';
      try {
        const startOp = post({
          apiName: 'worktimerApi',
          path: '/active-sessions',
          options: { body: { task_id: newTaskId, start_at: now.toISOString() } }
        });
        await startOp.response;
      } catch {}

      // localStorageを新しいタスクで更新
      localStorage.setItem(PENDING_SESSION_KEY, JSON.stringify({
        task_id: newTaskId,
        task_name: newTaskName,
        start_at: now.toISOString(),
      }));

      // UIを新しいタスクでリセット（isActive は true のまま）
      setSelectedTaskId(newTaskId);
      setStartTime(now);
      setSeconds(0);
      setCompletedCount(0);
      fetchTodayAssignment(newTaskId);
    } catch (e) {
      console.error("❌ タスク切り替えエラー:", e);
      alert("タスクの切り替えに失敗しました");
    } finally {
      setIsSaving(false);
      isRequestInProgress.current = false;
    }
  };

  // 未保存セッションを保存する
  const handleRecoverySave = async () => {
    if (!pendingSession) return;
    setIsSaving(true);
    try {
      const op = post({
        apiName: 'worktimerApi',
        path: '/worklogs',
        options: {
          body: {
            task_id: pendingSession.task_id,
            start_at: pendingSession.start_at,
            end_at: pendingSession.end_at,
          }
        }
      });
      await op.response;

      // アクティブセッションも削除
      try {
        const delOp = del({ apiName: 'worktimerApi', path: '/active-sessions' });
        await delOp.response;
      } catch {}

      localStorage.removeItem(PENDING_SESSION_KEY);
      setPendingSession(null);
      alert("✅ 作業データを保存しました");
    } catch (e) {
      console.error("❌ 復旧保存エラー:", e);
      alert("保存に失敗しました");
    } finally {
      setIsSaving(false);
    }
  };

  // 休憩ボタン: 現在のworklogを保存してタイマー停止（タスク選択は維持）
  const handleBreak = async () => {
    if (!isActive || isSaving || isRequestInProgress.current) return;

    isRequestInProgress.current = true;
    setIsSaving(true);
    setIsActive(false);

    const endTime = new Date();
    const currentStartTime = startTime || new Date();

    try {
      const savedSessionCount = completedCount;
      const op = post({
        apiName: 'worktimerApi',
        path: '/worklogs',
        options: {
          body: {
            task_id: selectedTaskId,
            start_at: currentStartTime.toISOString(),
            end_at: endTime.toISOString(),
            completed_count: savedSessionCount,
          }
        }
      });
      await op.response;

      try {
        const delOp = del({ apiName: 'worktimerApi', path: '/active-sessions' });
        await delOp.response;
      } catch {}

      localStorage.removeItem(PENDING_SESSION_KEY);
      setSeconds(0);
      // 休憩後も合計完了数を維持（セッション分をベースに加算してリセット）
      setTodayBaseCount(prev => prev + savedSessionCount);
      setCompletedCount(0);
      // selectedTaskId はそのまま維持（休憩後に再開しやすいように）
    } catch (e) {
      console.error('❌ 休憩保存エラー:', e);
      alert('保存に失敗しました。');
    } finally {
      setIsSaving(false);
      isRequestInProgress.current = false;
    }
  };

  // 未保存セッションを破棄する
  const handleRecoveryDiscard = async () => {
    try {
      const delOp = del({ apiName: 'worktimerApi', path: '/active-sessions' });
      await delOp.response;
    } catch {}
    localStorage.removeItem(PENDING_SESSION_KEY);
    setPendingSession(null);
  };

  const formatTime = (totalSeconds) => {
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return [hrs, mins, secs].map(v => v < 10 ? "0" + v : v).join(":");
  };

  const handleToggle = async () => {
    console.log("🔵 handleToggle called", { 
      isActive, 
      isSaving, 
      isRequestInProgress: isRequestInProgress.current 
    });

    if (!isActive) {
      if (!selectedTaskId) {
        alert("タスクを選択してください");
        return;
      }
      if (isStarting) return;
      const now = new Date();
      setIsStarting(true);

      // アクティブセッションをDBに登録（重複チェック含む）
      try {
        const op = post({
          apiName: 'worktimerApi',
          path: '/active-sessions',
          options: { body: { task_id: selectedTaskId, start_at: now.toISOString() } }
        });
        await op.response;
      } catch (e) {
        // 409: 別端末・別ブラウザで既に計測中
        // Amplify v6 REST API は $metadata.httpStatusCode にステータスコードが入る
        let isConflict = false;
        try { isConflict = e?.$metadata?.httpStatusCode === 409; } catch {}
        if (isConflict) {
          const forceStop = await showConfirm(
            '⚠️',
            '別の端末で計測中です',
            '別の端末またはブラウザで既に計測が開始されています。\n\n強制停止してこの端末で計測を開始しますか？\n（強制停止した端末の計測データは保存されません）',
            '強制停止して開始',
            'キャンセル',
            true
          );
          if (!forceStop) { setIsStarting(false); return; }
          // 強制停止：既存セッションをDBから削除してから再登録
          try {
            const delOp = del({ apiName: 'worktimerApi', path: '/active-sessions' });
            await delOp.response;
            const retryOp = post({
              apiName: 'worktimerApi',
              path: '/active-sessions',
              options: { body: { task_id: selectedTaskId, start_at: now.toISOString() } }
            });
            await retryOp.response;
          } catch (forceErr) {
            console.error("❌ 強制停止エラー:", forceErr);
            await showAlert('❌', '強制停止に失敗しました', '再度お試しください。');
            setIsStarting(false);
            return;
          }
        } else {
          console.error("❌ アクティブセッション登録エラー:", e);
          await showAlert('❌', '計測を開始できませんでした', '通信エラーが発生しました。再度お試しください。');
          setIsStarting(false);
          return;
        }
      }

      // API成功後にタイマーを起動
      setIsStarting(false);
      setIsActive(true);
      setStartTime(now);
      setCompletedCount(0);
      fetchTodayAssignment(selectedTaskId);

      // ブラウザ終了に備えてlocalStorageにセッション情報を保存
      const taskName = tasks.find(t => t.task_id === selectedTaskId)?.task_name || '';
      localStorage.setItem(PENDING_SESSION_KEY, JSON.stringify({
        task_id: selectedTaskId,
        task_name: taskName,
        start_at: now.toISOString(),
      }));
    } else {
      if (isSaving || isRequestInProgress.current) {
        console.log("⚠️ Request already in progress, blocking duplicate");
        return;
      }

      console.log("🟢 Starting save operation");
      isRequestInProgress.current = true;
      setIsSaving(true); 
      setIsActive(false);

      const endTime = new Date();
      const currentStartTime = startTime || new Date(); 

      try {
        console.log("📤 Sending POST request...");
        const restOperation = post({
          apiName: 'worktimerApi',
          path: '/worklogs',
          options: {
            body: {
              task_id: selectedTaskId,
              start_at: currentStartTime.toISOString(),
              end_at: endTime.toISOString(),
              completed_count: completedCount,
            }
          }
        });

        const response = await restOperation.response;
        console.log("✅ 保存成功:", response);

        // アクティブセッションをDBから削除（リアルタイム監視用）
        try {
          const delOp = del({ apiName: 'worktimerApi', path: '/active-sessions' });
          await delOp.response;
        } catch (e) {
          console.error("❌ アクティブセッション削除エラー:", e);
        }

        // 正常に保存できたのでlocalStorageの未保存データをクリア
        localStorage.removeItem(PENDING_SESSION_KEY);

        setSeconds(0);
        setSelectedTaskId("");
        setCompletedCount(0);
        setTodayBaseCount(0);
        setTodayAssignment(null);
      } catch (e) {
        console.error("❌ 保存中にエラーが発生しました:", e);
        alert("保存に失敗しました。");
      } finally {
        console.log("🔓 Releasing lock");
        setIsSaving(false);
        isRequestInProgress.current = false;
      }
    }
  };

  // 表示するユーザー名を取得
  const getDisplayName = () => {
    // データベースから取得したdisplay_nameまたはusernameを優先
    if (dbUser?.display_name) return dbUser.display_name;
    if (dbUser?.username) return dbUser.username;
    if (dbUser?.email) return dbUser.email.split('@')[0];
    
    // フォールバック: Cognitoの情報
    if (user?.username) return user.username;
    if (user?.signInDetails?.loginId) return user.signInDetails.loginId.split('@')[0];
    
    return 'ユーザー';
  };

  // ローディング中
  if (loading) {
    return (
      <div className="app-container app-loading-screen">
        <div className="app-spinner"></div>
      </div>
    );
  }

  // MFA必須設定オーバーレイ
  if (isAuthenticated && mfaRequired) {
    return (
      <div className="mfa-required-overlay">
        <div className="mfa-required-dialog">
          <div className="mfa-required-logo">
            <img src="/factime_logo.jpg" alt="FACTime" style={{ height: 40, borderRadius: 8 }} />
          </div>
          <h2 className="mfa-required-title">二段階認証の設定が必要です</h2>

          {mfaSetupStep === 'intro' && dbUser?.role === 'admin' && (
            <>
              {passkeyReRegister && (
                <p className="mfa-required-desc">
                  <strong>ログイン用のアドレスが factime.jp に変わったため、パスキーの登録し直しが必要です。</strong>
                </p>
              )}
              <p className="mfa-required-desc">
                このアカウントは管理者権限のため、パスワードの代わりに生体認証（指紋・顔認証・Windows Hello）
                またはセキュリティキーでログインする「パスキー」の登録が必須です。<br />
                お使いのPCまたはスマートフォンで登録してください。
              </p>
              {mfaError && <div className="mfa-required-error">{mfaError}</div>}
              <button className="mfa-required-btn" onClick={startWebAuthnSetup} disabled={mfaLoading}>
                {mfaLoading ? '登録中...' : 'パスキーを登録する →'}
              </button>
              <button className="mfa-required-logout" onClick={handleLogout}>ログアウト</button>
            </>
          )}

          {mfaSetupStep === 'admin-totp' && (
            <>
              {dbUser?.mfa_type === 'webauthn' && dbUser?.mfa_enabled && (
                <div className="mfa-required-success">✓ パスキーは登録済みです</div>
              )}
              <p className="mfa-required-desc">
                管理者アカウントでは、<strong>認証アプリ（Google Authenticator など）の登録も必須</strong>です。<br />
                パスキーが使えないときにパスワードでログインすると、認証アプリの6桁のコードが求められます。
                パスワードが漏れても、第三者が管理者としてログインできないようにするためです。<br />
                普段のパスキーでのログインは、これまでどおりコードの入力は不要です。
              </p>
              {mfaError && <div className="mfa-required-error">{mfaError}</div>}
              <button className="mfa-required-btn" onClick={startMfaSetup} disabled={mfaLoading}>
                {mfaLoading ? '準備中...' : '認証アプリを登録する →'}
              </button>
              <button className="mfa-required-logout" onClick={handleLogout}>ログアウト</button>
            </>
          )}

          {mfaSetupStep === 'intro' && dbUser?.role !== 'admin' && (
            <>
              <p className="mfa-required-desc">
                セキュリティ強化のため、このアカウントでは二段階認証（MFA）が必須です。<br />
                Google Authenticator などの認証アプリを使って設定してください。
              </p>
              {mfaError && <div className="mfa-required-error">{mfaError}</div>}
              <button className="mfa-required-btn" onClick={startMfaSetup} disabled={mfaLoading}>
                {mfaLoading ? '準備中...' : 'MFAを設定する →'}
              </button>
              <button className="mfa-required-logout" onClick={handleLogout}>ログアウト</button>
            </>
          )}

          {mfaSetupStep === 'scan' && (
            <>
              <p className="mfa-required-desc">
                認証アプリでQRコードをスキャンし、表示された6桁のコードを入力してください。
              </p>
              {mfaQrUrl && <img src={mfaQrUrl} alt="QR Code" className="mfa-required-qr" />}
              <div className="mfa-required-secret">
                <span className="mfa-required-secret-label">手動入力コード：</span>
                <code>{mfaSecret}</code>
              </div>
              <input
                type="text"
                className="mfa-required-code-input"
                placeholder="6桁のコード"
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                inputMode="numeric"
              />
              {mfaError && <div className="mfa-required-error">{mfaError}</div>}
              <button className="mfa-required-btn" onClick={verifyMfaSetup} disabled={mfaLoading || mfaCode.length !== 6}>
                {mfaLoading ? '確認中...' : '確認して有効化'}
              </button>
              <button className="mfa-required-logout" onClick={handleLogout}>ログアウト</button>
            </>
          )}

          {mfaSetupStep === 'done' && dbUser?.role === 'admin' && (
            <>
              <div className="mfa-required-success">✓ 二段階認証の設定が完了しました！</div>
              <p className="mfa-required-desc">
                次回からはパスキーでログインできます（パスワード入力は不要です）。<br />
                パスキーが使えないときは、パスワードと認証アプリのコードでログインしてください。<br />
                端末紛失対策として、設定画面からもう1台の端末のパスキーを追加登録しておくことをおすすめします。
              </p>
              <button className="mfa-required-btn" onClick={() => setMfaRequired(false)}>
                アプリを開始する →
              </button>
            </>
          )}

          {mfaSetupStep === 'done' && dbUser?.role !== 'admin' && (
            <>
              <div className="mfa-required-success">✓ 二段階認証の設定が完了しました！</div>
              <p className="mfa-required-desc">次回のログインからMFAコードが要求されます。</p>
              <button className="mfa-required-btn" onClick={() => setMfaRequired(false)}>
                アプリを開始する →
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // 未認証の場合
  if (!isAuthenticated) {
    if (showLanding) {
      return (
        <LandingPage
          onLogin={() => { setLoginInitialMode('login'); setShowLanding(false); }}
          onSignup={() => { setLoginInitialMode('signup'); setShowLanding(false); }}
        />
      );
    }
    return (
      <Login
        onLoginSuccess={handleLoginSuccess}
        initialMode={loginInitialMode}
        onShowLanding={() => setShowLanding(true)}
        initialError={loginError}
      />
    );
  }

  // 日時フォーマットヘルパー
  const fmtDateTime = (iso) => {
    if (!iso) return '不明';
    const d = new Date(iso);
    return d.toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // 認証済みの場合、メインアプリを表示
  return (
    <div className="app-container">

      {/* カスタムダイアログ */}
      {appDialog && (
        <div className="app-dialog-overlay" onClick={appDialog.onCancel || appDialog.onConfirm}>
          <div className="app-dialog" onClick={e => e.stopPropagation()}>
            {appDialog.icon && <div className="app-dialog-icon">{appDialog.icon}</div>}
            <h3 className="app-dialog-title">{appDialog.title}</h3>
            <p className="app-dialog-message">
              {appDialog.message.split('\n').map((line, i) => (
                <span key={i}>{line}{i < appDialog.message.split('\n').length - 1 && <br />}</span>
              ))}
            </p>
            <div className="app-dialog-actions">
              {appDialog.cancelLabel && (
                <button className="app-dialog-btn app-dialog-btn--cancel" onClick={appDialog.onCancel}>
                  {appDialog.cancelLabel}
                </button>
              )}
              <button
                className={`app-dialog-btn app-dialog-btn--confirm${appDialog.danger ? ' danger' : ''}`}
                onClick={appDialog.onConfirm}
              >
                {appDialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 未保存セッション復旧ダイアログ */}
      {pendingSession && (
        <div className="recovery-overlay">
          <div className="recovery-dialog">
            <div className="recovery-icon">⚠️</div>
            <h3>未保存の作業データがあります</h3>
            <p className="recovery-desc">
              前回ブラウザを閉じた際に、作業データが保存されませんでした。
            </p>
            <div className="recovery-detail">
              <div className="recovery-row">
                <span className="recovery-label">タスク</span>
                <span className="recovery-value">{pendingSession.task_name || '不明'}</span>
              </div>
              <div className="recovery-row">
                <span className="recovery-label">開始</span>
                <span className="recovery-value">{fmtDateTime(pendingSession.start_at)}</span>
              </div>
              <div className="recovery-row">
                <span className="recovery-label">終了</span>
                <span className="recovery-value">{fmtDateTime(pendingSession.end_at)}</span>
              </div>
            </div>
            <div className="recovery-actions">
              <button
                className="recovery-btn-discard"
                onClick={handleRecoveryDiscard}
                disabled={isSaving}
              >
                破棄する
              </button>
              <button
                className="recovery-btn-save"
                onClick={handleRecoverySave}
                disabled={isSaving}
              >
                {isSaving ? '保存中...' : '💾 保存する'}
              </button>
            </div>
          </div>
        </div>
      )}
    <div className="header">
      <img src="/factime_logo.jpg" alt="FACTime" className="header-logo" />
      <div className="header-buttons">
        <nav className="header-nav">
          <button
            className={`header-nav-btn ${currentPage === 'home' ? 'active' : ''}`}
            onClick={() => navigate('home')}
          >
            <span className="nav-icon">🏠</span><span className="nav-label">HOME</span>
          </button>
          <button
            className={`header-nav-btn ${currentPage === 'timer' ? 'active' : ''}`}
            onClick={() => navigate('timer')}
          >
            <span className="nav-icon">⏱️</span><span className="nav-label">タイマー</span>
          </button>
          <button
            className={`header-nav-btn ${currentPage === 'dashboard' ? 'active' : ''}`}
            onClick={() => navigate('dashboard')}
          >
            <span className="nav-icon">📊</span><span className="nav-label">ダッシュボード</span>
          </button>
          {dbUser?.role === 'admin' && (
            <button
              className={`header-nav-btn ${currentPage === 'videoManuals' ? 'active' : ''}`}
              onClick={() => navigate('videoManuals')}
            >
              <span className="nav-icon">🎬</span><span className="nav-label nav-label-long">操作マニュアル生成</span><span className="nav-label-short">マニュアル</span>
            </button>
          )}
        </nav>
        <div className="user-menu-container" style={{ position: 'relative' }}>
          <button 
            className="settings-icon-btn" 
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            title="メニュー"
          >
            ⚙️
          </button>
          <UserMenu
            user={dbUser || user}
            isOpen={userMenuOpen}
            isOperator={isOperator}
            onClose={() => setUserMenuOpen(false)}
            onNavigate={(page) => {
              if (page === 'settings') {
                if (dbUser?.role !== 'admin') return;
                navigate('settings');
              } else if (page === 'profile') {
                navigate('profile');
              } else if (page === 'operator') {
                if (!isOperator) return;
                navigate('operator');
              }
            }}
            onLogout={handleLogout}
          />
        </div>
      </div>
    </div>


      {currentPage === 'home' ? (
        <Home
          user={dbUser || user}
          onStartTask={handleStartTaskFromHome}
          onNavigate={navigate}
        />
      ) : currentPage === 'timer' ? (
        <div className="container">
          <div className="task-card">
            <h2>{isActive ? "🚀 作業中..." : "💤 待機中"}</h2>
            
            <div className="task-buttons">
              {tasksLoading ? (
                <p className="task-empty" style={{ color: '#9CA3AF' }}>読み込み中...</p>
              ) : tasks.length === 0 ? (
                <p className="task-empty">タスクがありません</p>
              ) : (() => {
                // ツリー構造を構築（最大3階層対応）
                const itemMap = {};
                tasks.forEach(t => { itemMap[t.task_id] = { ...t, children: [] }; });
                const roots = [];
                tasks.forEach(t => {
                  if (t.parent_id && itemMap[t.parent_id]) {
                    itemMap[t.parent_id].children.push(itemMap[t.task_id]);
                  } else if (!t.parent_id) {
                    roots.push(itemMap[t.task_id]);
                  }
                });

                const renderItem = (item, depth = 0) => {
                  if (item.item_type !== 'folder') {
                    return (
                      <button
                        key={item.task_id}
                        className={`task-btn${depth > 0 ? ' task-btn--indented' : ''} ${selectedTaskId === item.task_id ? 'selected' : ''} ${isActive && selectedTaskId === item.task_id ? 'task-btn-active' : ''}`}
                        onClick={() => handleTaskChange(item.task_id)}
                        disabled={isSaving}
                        style={item.color ? { '--task-color': item.color } : {}}
                      >
                        {item.task_name}
                      </button>
                    );
                  }
                  // フォルダ：中身があるものだけ表示
                  const hasContent = item.children.length > 0;
                  if (!hasContent) return null;
                  return (
                    <div key={item.task_id} className={`task-folder-group${depth > 0 ? ' task-folder-group--nested' : ''}`}>
                      <div className="task-folder-label" style={depth > 0 ? { paddingLeft: `${10 + depth * 14}px` } : {}}>
                        {'　'.repeat(depth)}📁 {item.task_name}
                      </div>
                      <div className="task-folder-items">
                        {item.children.map(child => renderItem(child, depth + 1))}
                      </div>
                    </div>
                  );
                };

                return <>{roots.map(item => renderItem(item))}</>;
              })()}
            </div>

            <button
              className="btn break"
              onClick={handleBreak}
              disabled={!isActive || isSaving}
            >
              ☕ 休憩
            </button>

            {selectedTaskId && (
              <div className="timer-task-name">
                {tasks.find(t => t.task_id === selectedTaskId)?.task_name || ''}
              </div>
            )}
            <div className="timer-display">{formatTime(seconds)}</div>

            {/* 作業カウント（タスク選択後に表示） */}
            {selectedTaskId && (
              <div className="count-area">
                <button
                  className="btn-count-down"
                  onClick={handleCountDown}
                  disabled={!isActive || completedCount <= 0}
                  title="取り消し -1"
                >
                  －
                </button>
                <div className="count-progress">
                  <span className="count-label">タスク完了数</span>
                  <span className="count-value">
                    <span className="count-done">{todayBaseCount + completedCount}</span>
                    {todayAssignment && todayAssignment.assigned_count > 0 && (
                      <span className="count-total"> / {todayAssignment.assigned_count}</span>
                    )}
                  </span>
                </div>
                <button
                  className="btn-count-up"
                  onClick={handleCountUp}
                  disabled={!isActive}
                  title="タスク完了 +1"
                >
                  ＋
                </button>
              </div>
            )}

            <button
              className={`btn ${isActive ? 'stop' : 'start'}`}
              onClick={handleToggle}
              disabled={isSaving || isStarting}
            >
              {isSaving ? '⏳ SAVING...' : isStarting ? (
                <span className="btn-spinner-wrap">
                  <span className="btn-spinner" />
                  STARTING...
                </span>
              ) : (isActive ? '⏹ STOP MEASURING' : '▶ START WORKING')}
            </button>

            <label className="wakelock-toggle">
              <input
                type="checkbox"
                checked={wakeLockEnabled}
                onChange={e => setWakeLockEnabled(e.target.checked)}
              />
              <span>📱 スリープ防止{!('wakeLock' in navigator) ? '（非対応）' : wakeLockEnabled ? ' ON' : ' OFF'}</span>
            </label>
          </div>

        </div>
      ) : currentPage === 'settings' ? (
        <Settings user={dbUser || user} onBack={() => navigate('home')} onTasksUpdated={fetchTasks} />
      ) : currentPage === 'profile' ? (
        <UserProfile
          user={dbUser || user}
          onBack={() => navigate('home')}
          onEmailChanged={(email) => setDbUser(prev => prev ? { ...prev, email } : prev)}
        />
      ) : currentPage === 'videoManuals' ? (
        <VideoManualGenerator />
      ) : currentPage === 'operator' && isOperator ? (
        <OperatorConsole currentUser={dbUser} onBack={() => navigate('home')} />
      ) : (
        <Dashboard user={dbUser || user} />
      )}
    </div>
  );
}

export default App;